// ============================================================================
// 🔐 AUTH STORE
//    💡 Store de autenticación y sesión:
//       - Login estándar y modo kiosko
//       - Almacenamiento del token JWT, usuario y permisos
//       - Restauración y cambio de contraseña
//       - Persistencia de sesión en localStorage
// ============================================================================

import { defineStore } from "pinia";
import type { PersistenceOptions } from "pinia-plugin-persistedstate";
import { getEndpoints } from "@/services/apiServices/endpoints";
import {
  extractErrorMessage,
  fetchPostData,
} from "@/services/apiServices/http";
import {
  advanceAuthGeneration,
  closingSession,
  currentAuthGeneration,
} from "./authSessionState";
import { usePushNotifications } from "@/composables/pwa/usePushNotifications";

// Cierre de sesión en curso (SG-AUD-19): estado TRANSITORIO en `authSessionState.ts`, fuera del estado de
// la store para que el plugin de persistencia (que escribe el estado completo) nunca lo guarde.
/**
 * Logout en vuelo (SG-AUD-20): un único cierre compartido. Las llamadas concurrentes a `logOut()` se unen a
 * esta promesa, de modo que hay una sola notificación al backend, una sola limpieza y el indicador
 * `closingSession` se libera una sola vez, al terminar la operación compartida. Transitorio, no se persiste.
 */
let inFlightLogout: Promise<boolean> | null = null;

/**
 * Generación de sesión: identificador aleatorio y opaco de UN login. Se crea en cada login correcto (aunque
 * el JWT coincida con el anterior) y se invalida al cerrar sesión. No deriva del JWT ni de credenciales y no
 * lo duplica: solo permite a los consumidores distinguir una sesión de otra (p. ej. SofyGastos aísla sus
 * efectos asíncronos). No es un secreto ni una frontera de seguridad.
 */
function newSessionId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(bytes);
  else
    for (let i = 0; i < bytes.length; i++) bytes[i] = (Math.random() * 256) | 0;
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// 🎯 Tipado del estado de autenticación
interface IAuthState {
  error: string;
  jwt: string;
  /** Generación de sesión (ver `newSessionId`); vacía sin sesión o en sesiones persistidas antiguas. */
  sessionId: string;
  user: string;
  remember: boolean;
  userShortName: string;
  userId: number | null;
  userLocalId: number | null;
  userPic: string;
  userPermissions: Record<string, boolean>;
  msgResetPassword: string;
  msgUpdatingPassword: string;
}

export const useAuthStore = defineStore("auth", {
  // 📦 Estado inicial
  state: (): IAuthState => ({
    error: "",
    jwt: "",
    sessionId: "",
    user: "",
    remember: JSON.parse(localStorage.getItem("rememberSetup") || "false"),
    userShortName: "",
    userId: null,
    userLocalId: null,
    userPic: "",
    userPermissions: {},
    msgResetPassword: "",
    msgUpdatingPassword: "",
  }),

  // 🔍 Getters
  getters: {
    getJwt: (state) => state.jwt,
    getSessionId: (state) => state.sessionId,
    /** `true` desde que empieza un logout hasta que termina su limpieza (no se persiste). */
    getIsLoggingOut: () => closingSession.value,
    getUser: (state) => state.user,
    getError: (state) => state.error,
    getUserShortName: (state) => state.userShortName,
    getUserId: (state) => state.userId,
    getUserLocalId: (state) => state.userLocalId,
    getUserPic: (state) => state.userPic,
    getMsgResetPassword: (state) => state.msgResetPassword,
    getMsgUpdatingPassword: (state) => state.msgUpdatingPassword,
    getUserPermissions: (state) => state.userPermissions,
    getRemember: (state) => state.remember,
  },

  // ⚙️ Acciones
  actions: {
    /**
     * 🔐 Login estándar con usuario y contraseña
     */
    async login(
      user: string,
      company: string,
      password: string,
      rememberMe: boolean,
      isCustomer: boolean,
    ): Promise<boolean> {
      const URL = getEndpoints().login;
      const credentials = {
        username: user,
        claveEmpresa: company,
        password,
        esCliente: isCustomer,
      };

      try {
        const data = await fetchPostData<any>(URL, "", credentials);

        if (!data.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.sessionId = newSessionId();
        advanceAuthGeneration();
        this.user = data.empleado.nombre;
        this.userShortName = data.empleado.nombreCorto;
        this.userId = data.empleado.id;
        this.userLocalId = data.empleado.idLocal;
        this.userPic = data.empleado.foto;

        const permisosApp = data.empleado?.permisosApp ?? {};

        // 🧾 Mapeo de permisos según tipo de usuario

        // si es cliente solo ve SofyGerencia
        if (isCustomer) {
          this.userPermissions = {
            permisoConsultaInicio: true,
            permisoConsultaAgenda: false,
            permisoConsultaFichajes: false,
            permisoConsultaGastos: false,
            permisoConsultaClientes: false,
            permisoConsultaPedidos: false,
            permisoConsultaPedidoInterno: false,
            permisoConsultaTransporte: false,
            permisoConsultaMensajeria: false,
            permisoConsultaLocalizador: false,
            permisoColsultaIntervenciones: false,
            permisoConsultaGerencia: true,
          };
        } else {
          this.userPermissions = {
            permisoConsultaInicio: true,
            permisoConsultaAgenda: !!permisosApp.tieneAppAgenda,
            permisoConsultaFichajes: !!permisosApp.tieneAppFichajes,
            permisoConsultaGastos: !!permisosApp.tieneAppGastos,
            permisoConsultaClientes: false,
            permisoConsultaPedidos: false,
            permisoConsultaPedidoInterno:
              permisosApp.tieneAppPedidoInterno === true,
            permisoConsultaTransporte: false,
            permisoConsultaMensajeria: !!permisosApp.tieneAppMensajeria,
            permisoConsultaLocalizador: !!permisosApp.tieneAppLocalizador,
            permisoColsultaIntervenciones: !!permisosApp.tieneAppIntervenciones,
            permisoConsultaGerencia: !!permisosApp.tieneAppGerencia,
          };
        }

        this.remember = rememberMe;
        this.error = "";
        return true;
      } catch (err: any) {
        console.error("Error en login:", err);
        this.error = extractErrorMessage(err);
        return false;
      }
    },

    /**
     * 🔢 Login por PIN (EAN tarjeta) — SofyApps
     *    ✅ No toca kioskLogin (otra PWA)
     *    Payload: { eanTarjeta, claveEmpresa }
     *    Response: igual que login normal (token + empleado + permisosApp)
     */
    async loginPin(
      eanTarjeta: string,
      claveEmpresa: string,
      rememberMe: boolean,
    ): Promise<boolean> {
      const URL = getEndpoints().loginPin; // ✅ endpoint NUEVO de SofyApps
      const credentials = { eanTarjeta, claveEmpresa };

      try {
        const data = await fetchPostData<any>(URL, "", credentials);

        if (!data?.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.sessionId = newSessionId();
        advanceAuthGeneration();
        this.user = data.empleado?.nombre ?? "";
        this.userShortName = data.empleado?.nombreCorto ?? "";
        this.userId =
          typeof data.empleado?.id === "number" ? data.empleado.id : null;
        this.userPic = data.empleado?.foto ?? "";

        // ✅ Igual que login normal: permisos completos
        this.userPermissions = {
          permisoConsultaInicio: true,
          permisoConsultaAgenda: !!data.empleado?.permisosApp?.tieneAppAgenda,
          permisoConsultaFichajes:
            !!data.empleado?.permisosApp?.tieneAppFichajes,
          permisoConsultaGastos: !!data.empleado?.permisosApp?.tieneAppGastos,
          permisoConsultaClientes: false,
          permisoConsultaPedidos: false,
          permisoConsultaPedidoInterno:
            data.empleado?.permisosApp?.tieneAppPedidoInterno === true,
          permisoConsultaTransporte: false,
          permisoConsultaMensajeria:
            !!data.empleado?.permisosApp?.tieneAppMensajeria,
          permisoConsultaLocalizador:
            !!data.empleado?.permisosApp?.tieneAppLocalizador,
          permisoColsultaIntervenciones:
            !!data.empleado?.permisosApp?.tieneAppIntervenciones,
          permisoConsultaGerencia:
            !!data.empleado?.permisosApp?.tieneAppGerencia,
        };

        this.remember = rememberMe;
        this.error = "";
        return true;
      } catch (err: any) {
        this.error = extractErrorMessage(err);
        return false;
      }
    },

    /**
     * 🔓 Login desde modo kiosko mediante PIN + empresa
     */
    async kioskLogin(pin: string, company: string): Promise<boolean> {
      const URL = getEndpoints().kioskLogin;
      const credentials = { eanTarjeta: pin, claveEmpresa: company };

      try {
        const data = await fetchPostData<any>(URL, "", credentials);

        if (!data.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.sessionId = newSessionId();
        advanceAuthGeneration();
        this.user = data.empleado.nombre;
        this.userShortName = data.empleado.nombreCorto;
        this.userId = data.empleado.id;
        this.userPic = data.empleado.foto;
        return true;
      } catch (err: any) {
        this.error = extractErrorMessage(err);
        return false;
      }
    },

    /**
     * 🚪 Logout del usuario, limpieza total del estado
     */
    async logOut() {
      // Un logout ya en curso es el único: quien llega después se une a él (no se inicia otro).
      if (inFlightLogout) return inFlightLogout;
      // Se marca ANTES de cualquier await: desde este instante la sesión no es utilizable.
      closingSession.value = true;
      inFlightLogout = this.closeSession().finally(() => {
        closingSession.value = false;
        inFlightLogout = null;
      });
      return inFlightLogout;
    },

    /** Cuerpo del logout (el token se conserva aparte para el aviso al backend y la baja push). */
    async closeSession() {
      const URL = getEndpoints().logout;
      const token = this.jwt;
      // Generación de autenticación bajo la que empieza este cierre (SG-AUD-21).
      const startedUnder = currentAuthGeneration();
      // La sesión deja de existir para los consumidores en cuanto empieza el cierre, no al terminar las
      // llamadas de red de abajo.
      this.sessionId = "";

      const { unsubscribe } = usePushNotifications();

      try {
        // 🔔 eliminar suscripción push si existe
        await unsubscribe().catch((err) => {
          console.warn("No se pudo eliminar la suscripción push", err);
        });

        // 🔐 avisar al backend del logout
        if (token) {
          await fetchPostData(URL, token, {});
        }
      } catch (err) {
        console.warn(
          "No se pudo notificar el cierre de sesión al servidor",
          err,
        );
      }

      // Una autenticación correcta (password, PIN o kiosk, aunque devuelva el mismo JWT) mientras este
      // logout estaba pendiente es una sesión NUEVA que este logout, ya obsoleto, no debe borrar: solo se
      // notificó al backend el token anterior (SG-AUD-20). Se decide por la generación de autenticación,
      // que solo avanza al autenticar: rehidratar la sesión original (o una legacy) la restaura pero no la
      // avanza, así que ese logout sigue limpiando (SG-AUD-21).
      if (currentAuthGeneration() !== startedUnder) return true;

      // 🔄 Limpieza del estado
      this.jwt = "";
      this.sessionId = "";
      this.user = "";
      this.userId = null;
      this.userPic = "";
      this.userShortName = "";
      this.userPermissions = {};
      this.remember = false;

      // AUTH-WU-002: autenticación y configuración son ciclos de vida
      // independientes. Conservamos la configuración completa en el mismo
      // ámbito elegido por el usuario mientras limpiamos la sesión auth.
      const rememberSetup =
        localStorage.getItem("rememberSetup") === "true";
      const preferredStorage = rememberSetup
        ? localStorage
        : sessionStorage;
      const fallbackStorage = rememberSetup
        ? sessionStorage
        : localStorage;
      const readConfig = (key: string) =>
        preferredStorage.getItem(key) ?? fallbackStorage.getItem(key);
      const company = readConfig("company");
      const apiURL = readConfig("apiURL");
      const apiFichajesURL = readConfig("apiFichajesURL");
      const apiRecursosHumanosURL = readConfig(
        "apiRecursosHumanosURL",
      );
      const kioskCompany = localStorage.getItem("kiosk-company");
      const kioskApiUrl = localStorage.getItem("kiosk-api-url");

      localStorage.clear();
      sessionStorage.clear();

      const configStorage = rememberSetup
        ? localStorage
        : sessionStorage;
      if (company) configStorage.setItem("company", company);
      if (apiURL) configStorage.setItem("apiURL", apiURL);
      if (apiFichajesURL)
        configStorage.setItem("apiFichajesURL", apiFichajesURL);
      if (apiRecursosHumanosURL)
        configStorage.setItem(
          "apiRecursosHumanosURL",
          apiRecursosHumanosURL,
        );
      if (kioskCompany)
        localStorage.setItem("kiosk-company", kioskCompany);
      if (kioskApiUrl)
        localStorage.setItem("kiosk-api-url", kioskApiUrl);
      localStorage.setItem("rememberSetup", rememberSetup.toString());

      return true;
    },

    /**
     * 🧪 Setter manual de token (útil para restaurar sesión desde persistencia)
     */
    setJwt(token: string) {
      this.jwt = token;
    },

    /**
     * 🔁 Solicita envío de enlace de restauración de contraseña
     */
    async restorePassword(user: string, company: string): Promise<boolean> {
      const URL = getEndpoints().restorePassword;
      try {
        await fetchPostData<any>(URL, "", {
          login: user,
          claveEmpresa: company,
        });
        this.msgResetPassword =
          "Se ha enviado un enlace para restablecer su contraseña.";
        return true;
      } catch (err) {
        this.error = extractErrorMessage(err, "Credenciales incorrectas");
        return false;
      }
    },

    /**
     * 🔐 Actualización de contraseña tras recuperación
     */
    async passwordUpdating(
      token: string,
      newPassword: string,
      newPasswordRepeat: string,
    ): Promise<boolean> {
      const URL = getEndpoints().passwordUpdating;
      try {
        const data = await fetchPostData<any>(URL, "", {
          password: newPassword,
          passwordConfirmed: newPasswordRepeat,
          tokenRecovery: token,
        });

        this.msgUpdatingPassword = data.message;
        return true;
      } catch (err) {
        this.error = extractErrorMessage(err, "Ha ocurrido un error.");
        return false;
      }
    },
  },

  // 💾 Persistencia de estado entre recargas
  persist: {
    enabled: true,
    strategies: [
      {
        key: "auth",
        storage: localStorage,
        paths: [
          "jwt",
          "user",
          "remember",
          "userShortName",
          "userId",
          "userPic",
          "userPermissions",
        ],
      },
    ],
  } as PersistenceOptions,
});
